import { DatePipe } from "@angular/common";
import { Injectable, inject } from "@angular/core";
import { map } from "rxjs/operators";
import { ConstructAPI } from "src/app/API/constructAPI";
import { RestApiService } from "src/app/API/restapi";
import {
  Company,
  IAPICashBook,
  IAPICompany,
  IAPISqlInfo,
  ICashBook,
  ICompanyGroup,
  SqlInfo,
} from "src/model/company.model";

interface GetCompaniesOptions {
  sharable?: boolean;
  groupId?: number;
}

type IAPIAddCompany = IAPICompany & IAPICashBook;

@Injectable({
  providedIn: "root",
})
export class CompanyAdapter {
  private datePipe = inject(DatePipe);

  adapt(item: any): Company {
    return new Company(
      item.id,
      item.name,
      item.address,
      item.companyCode.trim(),
      item.contactNo,
      item.businessBranchId,
      item.regNo,
      item.group,
      new Date(item.dateOperating),
      item.unverifedCustomerCount,
      item.requireConsentForm,
      item.uid,
    );
  }

  adaptCompanyGroup(item: any): ICompanyGroup {
    return {
      id: item.id,
      name: item.name,
    };
  }

  adaptCompanySqlCode(item: any): SqlInfo {
    return new SqlInfo(item.companyCode, item.glCode, item.group, item.id);
  }

  toSqlCodeAPIModel(sqlInfo: SqlInfo): IAPISqlInfo {
    return {
      glCode: sqlInfo.GLCode,
    };
  }

  toApiModel(company: Company, cashbook?: ICashBook): IAPIAddCompany {
    return {
      name: company.Name,
      address: company.Address,
      companyCode: company.CompanyCode,
      ...(company.Group && { group: Number(company.Group) }),
      contactNo: company.ContactNo,
      businessBranchId: company.BranchId,
      regNo: company.RegNum,
      dateOperating: this.datePipe.transform(
        company.DateOperating,
        "yyyy-MM-dd",
      ),
      // ...(company.RequireConsentForm !== undefined && { requireConsentForm: company.RequireConsentForm }),
      ...(cashbook?.initialCashBookBalance && {
        initialCashBookBalance: Number(cashbook.initialCashBookBalance),
      }),
      ...(cashbook?.initialCashBookDate && {
        initialCashBookDate: cashbook.initialCashBookDate.toISOString(),
      }),
    };
  }
}

@Injectable({
  providedIn: "root",
})
export class CompanyService {
  private api = inject(RestApiService);
  private constructApi = inject(ConstructAPI);
  private adapter = inject(CompanyAdapter);

  getAllCompanies(o?: GetCompaniesOptions): Promise<Company[]> {
    const url =
      o?.groupId === undefined || o?.groupId === 0
        ? this.constructApi.getAllCompanies(o?.sharable)
        : this.constructApi.getCompanyGroupCompanies(o?.groupId);
    return new Promise<Company[]>((resolve, reject) => {
      this.api
        .get<Company[]>(url)
        .pipe(map((data) => data.map((d) => this.adapter.adapt(d))))
        .subscribe({
          next: (response) => resolve(response),
          error: (err) => {
            this.api.handleError(err);
            console.error("Failed to get all companies: ", err);
            reject(err);
          },
        });
    });
  }

  getCompanyDetails(companyId: number): Promise<Company> {
    return new Promise<Company>((resolve, reject) => {
      this.api
        .get<Company>(this.constructApi.getCompanyDetails(companyId))
        .pipe(map((data) => this.adapter.adapt(data)))
        .subscribe({
          next: (response) => resolve(response),
          error: (err) => {
            this.api.handleError(err);
            console.error("Failed to get company details: ", err);
            reject(err);
          },
        });
    });
  }

  getCompanyByUid(uid: string): Promise<Company> {
    return new Promise<Company>((resolve, reject) => {
      this.api
        .get(this.constructApi.getCompanyByUID(uid))
        .pipe(map((data) => this.adapter.adapt(data)))
        .subscribe({
          next: (response) => resolve(response),
          error: (err) => {
            this.api.handleError(err);
            console.error("Failed to get company details: ", err);
            reject(err);
          },
        });
    });
  }

  updateCompany(company: Company): Promise<Company> {
    return new Promise<Company>((resolve, reject) => {
      this.api
        .put<Company>(
          this.constructApi.getUpdateCompany(company.ID),
          this.adapter.toApiModel(company),
        )
        .subscribe({
          next: (response) => resolve(this.adapter.adapt(response)),
          error: (err) => {
            this.api.handleError(err);
            console.error("Failed to update company: ", err);
            reject(err);
          },
        });
    });
  }

  addCompany(company: Company, cashbook: ICashBook): Promise<Company> {
    return new Promise<Company>((resolve, reject) => {
      this.api
        .post<Company>(
          this.constructApi.getAddCompany(),
          this.adapter.toApiModel(company, cashbook),
        )
        .subscribe({
          next: (response) => resolve(this.adapter.adapt(response)),
          error: (err) => {
            this.api.handleError(err);
            console.error("Failed to add company: ", err);
            reject(err);
          },
        });
    });
  }

  getAllCompanyGroups(): Promise<ICompanyGroup[]> {
    return new Promise<ICompanyGroup[]>((resolve, reject) => {
      this.api
        .get<ICompanyGroup[]>(this.constructApi.getAllCompanyGroups())
        .pipe(map((data) => data.map((d) => this.adapter.adaptCompanyGroup(d))))
        .subscribe({
          next: (response) => resolve(response),
          error: (err) => {
            this.api.handleError(err);
            console.error("Failed to get all company groups: ", err);
            reject(err);
          },
        });
    });
  }

  getCompanySQLInfo(companyId: number) {
    return new Promise<SqlInfo>((resolve, reject) => {
      this.api
        .get(this.constructApi.getSqlInfoOfSingleCompany(companyId))
        .pipe(map((data) => this.adapter.adaptCompanySqlCode(data)))
        .subscribe({
          next: (response) => resolve(response),
          error: (err) => {
            this.api.handleError(err);
            console.error("Failed to get company details: ", err);
            reject(err);
          },
        });
    });
  }

  updateSQLInfo(companyId: number, sql: SqlInfo): Promise<SqlInfo> {
    return new Promise<SqlInfo>((resolve, reject) => {
      this.api
        .put<Company>(
          this.constructApi.getSqlInfoOfSingleCompany(companyId),
          this.adapter.toSqlCodeAPIModel(sql),
        )
        .subscribe({
          next: (response) =>
            resolve(this.adapter.adaptCompanySqlCode(response)),
          error: (err) => {
            this.api.handleError(err);
            console.error("Failed to update company: ", err);
            reject(err);
          },
        });
    });
  }
}
